/**
 * UI action → required-permission map.
 *
 * This is `ROUTE_PERMISSIONS` one level down. A route says "which keys open this screen"; an
 * action says "which keys allow this control". The argument is the same one that map already
 * makes in writing, and it holds harder here because there are far more controls than screens.
 *
 * ## Why the indirection exists
 *
 * Before this map, every control named a backend key directly — `permission: 'Group.Manage'`,
 * `<Can action="Device.Update">`. Two things follow from that, and both were already visible in
 * the repository rather than hypothetical.
 *
 * **A rename touches every call site.** 23 keys appeared in more than one file; `Device.View` in
 * eight of them.
 *
 * **A key that becomes more granular cannot be split mechanically, because the call site does not
 * record what it meant.** `permission: 'Group.Manage'` next to an Edit label means "edit" only
 * because a human can read the label; in `groupsScreen.tsx`'s header action there is no label to
 * read. The catalogue shows this is the direction of travel rather than a worry: `Lab`, `Device`,
 * `User`, `Certification` and `Session` are already split into verbs, while `Group`,
 * `CertificationProgram`, `Training` and `Maintenance` still carry a single `Manage`. `Institution`
 * is caught mid-split — it holds both `Manage` and `Update`.
 *
 * ## The rule that makes it work
 *
 * **Intent is primary; the key is an implementation detail of this file.** `'group.create'`,
 * `'group.edit'` and `'group.delete'` are three entries even though all three resolve to the same
 * `Group.Manage` today. That redundancy is not an oversight to tidy up — it is the seam a future
 * split lands in. Collapse them "because they are identical" and the next backend change is a
 * 30-file archaeology exercise again.
 *
 * The relation is therefore many-to-one now and one-to-one later, and no consumer notices.
 *
 * ## What belongs here and what does not
 *
 * An entry lists the keys the endpoint behind the control actually checks, and says so. Where the
 * backend leaves an endpoint on a bare `[Authorize]`, the gate is a product decision rather than a
 * mirror and is marked `UI-ONLY`, exactly as `ROUTE_PERMISSIONS` marks its own.
 *
 * Normative, unchanged: these checks are UI show/hide ONLY. The server is authoritative, and
 * gating a control here never makes an unguarded endpoint safe.
 */

import { hasPermission, type Subject } from './checks';
import type { PermissionKey } from './matrix.config';

export type ActionId =
  // Institutions
  | 'institution.create'
  | 'institution.edit'
  | 'institution.delete'
  | 'institution.editLogo'
  | 'institution.chooseAny'
  | 'institution.editStorageQuota'
  | 'institution.recalculateStorage'
  | 'institution.editStoragePool'
  // Groups
  | 'group.create'
  | 'group.edit'
  | 'group.delete'
  // Laboratories
  | 'laboratory.create'
  | 'laboratory.edit'
  | 'laboratory.delete'
  // Devices
  | 'device.create'
  | 'device.edit'
  | 'device.delete'
  | 'device.register'
  // Users
  | 'user.create'
  | 'user.edit'
  | 'user.delete'
  | 'user.move'
  | 'user.regeneratePassword'
  | 'user.manageScopes'
  | 'user.managePermissions'
  // User access (HS-1719)
  | 'userAccess.scopeAcrossInstitutions'
  // Certification programmes
  | 'certificationProgram.create'
  | 'certificationProgram.edit'
  | 'certificationProgram.delete'
  | 'certificationProgram.manageVideo'
  // Certification records
  | 'certification.viewAll'
  | 'certification.reset'
  // RFID cards
  | 'rfid.createCard'
  | 'rfid.deleteCard'
  | 'rfid.link'
  | 'rfid.unlink'
  | 'rfid.toggleActive'
  // Dashboard
  | 'dashboard.viewSummary'
  | 'dashboard.viewInstitutions'
  | 'dashboard.viewLabs'
  | 'dashboard.viewDevices'
  | 'dashboard.viewUsers'
  | 'dashboard.viewAlerts'
  | 'dashboard.assignAlert'
  | 'dashboard.viewInstallations'
  // Installations (HS-980)
  | 'installation.create'
  | 'installation.edit'
  | 'installation.cancel'
  | 'installation.delete'
  | 'installation.reassign'
  | 'installation.contactInstaller'
  // Service requests
  | 'serviceRequest.decide'
  | 'serviceRequest.assign'
  // Reports
  | 'report.generate'
  | 'report.download'
  | 'report.schedule'
  | 'report.delete'
  // My Files (portal)
  | 'file.upload'
  | 'file.delete';

/**
 * The keys each action needs — **any one** of them is enough, as with `ROUTE_PERMISSIONS`. An
 * empty list means "any authenticated subject".
 */
export const ACTION_PERMISSIONS: Record<ActionId, readonly PermissionKey[]> = {
  // ── Institutions ──────────────────────────────────────────────────────────────────────────
  'institution.create': ['Institution.Create'],
  // HS-1201: creating is Hawkins-only, but managing is not — an InstitutionAdmin holds
  // `Institution.Manage` for the one institution it belongs to and deliberately not `.Create`.
  'institution.edit': ['Institution.Manage'],
  'institution.delete': ['Institution.Manage'],
  // NOT a copy-paste slip: the logo card has always gated on `Institution.Update` while the rest
  // of the institution's editing gates on `Institution.Manage`, and the catalogue defines both.
  // Preserved verbatim so this migration changes no behaviour — but the disagreement is now in one
  // place instead of spread across four files, which is where it can actually be decided.
  'institution.editLogo': ['Institution.Update'],
  // "May this user pick an institution other than their own?" — the multi-key case that used to be
  // written out as `can(a) || can(b)` at three separate call sites.
  'institution.chooseAny': ['Institution.ViewAll', 'Institution.View'],
  // HS-1508, mirroring HS-1507's `InstitutionStorageController`: the default and per-user quota
  // PUTs and the recalculation POST ask `Institution.Manage`. Two entries for one key, on purpose
  // (see the sibling-actions rule in this package's CLAUDE.md).
  'institution.editStorageQuota': ['Institution.Manage'],
  'institution.recalculateStorage': ['Institution.Manage'],
  // The pool is Hawkins-only: the server asks this key only when `poolBytes` changes, and no role
  // is granted it, so only FullAccess passes.
  'institution.editStoragePool': ['Institution.ManageStoragePool'],

  // ── Groups ────────────────────────────────────────────────────────────────────────────────
  // UI-ONLY: `GET /api/Group` and `/api/Group/{id}` carry no permission attribute at all; writes
  // ask `Institution.AdministerHierarchy,Group.Manage`.
  //
  // All three resolve to one key today. They are three entries because the backend has split every
  // comparable domain already (`Lab`, `Device`, `User`), and when it splits this one the change
  // lands here and nowhere else.
  'group.create': ['Group.Manage'],
  'group.edit': ['Group.Manage'],
  'group.delete': ['Group.Manage'],

  // ── Laboratories ──────────────────────────────────────────────────────────────────────────
  'laboratory.create': ['Lab.Create'],
  'laboratory.edit': ['Lab.Update'],
  'laboratory.delete': ['Lab.Delete'],

  // ── Devices ───────────────────────────────────────────────────────────────────────────────
  'device.create': ['Device.Create'],
  'device.edit': ['Device.Update'],
  'device.delete': ['Device.Delete'],
  // Checked on the public kiosk route, where there is no provider — see `useDeviceRegisterAccess`,
  // which resolves this against a subject it builds from `/Auth/me` itself.
  'device.register': ['Device.Register'],

  // ── Users ─────────────────────────────────────────────────────────────────────────────────
  'user.create': ['User.Create'],
  // UI-ONLY: `PUT /api/User/{institutionId}/{userId}` carries no permission attribute — any
  // authenticated caller may edit any user in their institution. `User.Update` is the seeded key
  // for the intent, so it is the honest gate to show, and it is deliberately stricter than the API.
  'user.edit': ['User.Update'],
  'user.delete': ['User.Delete'],
  // HS-1288: `POST …/move` asks `Institution.AdministerHierarchy,User.Update` AND scope access to
  // both institutions — a pair check no client-side gate can mirror, so the screen degrades
  // honestly when the second institution is out of reach rather than pretending this covered it.
  'user.move': ['User.Update'],
  'user.regeneratePassword': ['User.Update'],
  'user.manageScopes': ['Scope.Assign'],
  'user.managePermissions': ['Permission.Assign'],

  // ── User access (HS-1719) ─────────────────────────────────────────────────────────────────
  // UI-ONLY: whether the grant scope tree offers every institution as a root rather than only the
  // user's own. AdminAPI has no key for "grant across institutions" and today rejects any
  // Group/Lab/Device outside the route institution (400). `Institution.Create` is held by
  // SmartSashAdmin alone — `Institution.ViewAll` is also held by InstitutionAdmin, so it cannot tell
  // the system administrator apart. Admin additionally holds it behind `CROSS_INSTITUTION_SCOPES`
  // until the backend accepts cross-institution scopes.
  'userAccess.scopeAcrossInstitutions': ['Institution.Create'],

  // ── Certification programmes ──────────────────────────────────────────────────────────────
  // `CertificationProgram.Manage` is the catalogue's only write key for programmes; there is no
  // separate `.Create`. Four entries for the same reason as Groups above.
  'certificationProgram.create': ['CertificationProgram.Manage'],
  'certificationProgram.edit': ['CertificationProgram.Manage'],
  'certificationProgram.delete': ['CertificationProgram.Manage'],
  'certificationProgram.manageVideo': ['CertificationProgram.Manage'],

  // ── Certification records ─────────────────────────────────────────────────────────────────
  // The per-user records behind the dashboard table — a different resource from the programmes
  // above, behind a different key.
  'certification.viewAll': ['Certification.ViewAll'],
  // Resetting revokes the user's valid certification records and abandons any open attempt
  // (`POST /api/Certification/reset`), so it is grouped with removing a record rather than with
  // editing one. The server enforces the same key; the action id only decides what is on screen.
  'certification.reset': ['Certification.Delete'],

  // ── RFID cards ────────────────────────────────────────────────────────────────────────────
  // Read off `sash-admin-api` @ `origin/development` (`6db1901`). Link and unlink are NOT one
  // permission — revoking is grouped with removing, not with granting (HS-1396):
  //   POST /api/rfid/create-card       → Rfid.CreateCard
  //   POST /api/rfid/remove            → Rfid.RemoveCard
  //   POST /api/rfidAssignment/assign  → Rfid.AssignCard
  //   POST /api/rfidAssignment/revoke  → Rfid.RemoveCard
  'rfid.createCard': ['Rfid.CreateCard'],
  'rfid.deleteCard': ['Rfid.RemoveCard'],
  'rfid.link': ['Rfid.AssignCard'],
  'rfid.unlink': ['Rfid.RemoveCard'],
  // `SetCardActive` really does check the create key, not a separate activation one.
  'rfid.toggleActive': ['Rfid.CreateCard'],

  // ── Dashboard ─────────────────────────────────────────────────────────────────────────────
  // Each card owns its read, so each is gated on the key its own endpoint checks (HS-1501), read
  // off `sash-admin-api` @ `origin/development` (`87e68b2`):
  //   GET /api/Report/summary → Report.View   (this entry used to say Institution.ViewAggregates |
  //                                            Institution.ViewAll, which the endpoint never checked;
  //                                            the route is /api/Dashboard/summary since HS-1659)
  //   GET /api/Institution    → Institution.Manage | Institution.ViewAggregates
  //   GET /api/Lab            → Lab.ViewAll
  //   GET /api/Device         → Device.ViewAll
  //   GET /api/User/list      → User.ViewAll
  'dashboard.viewSummary': ['Report.View'],
  'dashboard.viewInstitutions': ['Institution.Manage', 'Institution.ViewAggregates'],
  'dashboard.viewLabs': ['Lab.ViewAll'],
  'dashboard.viewDevices': ['Device.ViewAll'],
  'dashboard.viewUsers': ['User.ViewAll'],
  // HS-1541: the alert queue is the open Repair/Support service requests (`GET /api/service-requests`
  // → Request.View | Request.Manage); `PUT …/{id}/assignee` and `GET …/assignees` → Request.Manage.
  'dashboard.viewAlerts': ['Request.View', 'Request.Manage'],
  'dashboard.assignAlert': ['Request.Manage'],
  // UI-ONLY until the backend serves it: the installations list is mocked (`apps/admin/src/tmp/`),
  // and its contract proposes `Installation.Configure`; the read rides on `Report.View` meanwhile.
  // `Installation.View` now exists (HS-1543); moving the card onto it is HS-1544's, with the swap.
  'dashboard.viewInstallations': ['Report.View', 'Installation.Configure'],

  // ── Installations ─────────────────────────────────────────────────────────────────────────
  // HS-980, against the target contract (HS-1621): every write — create, edit, cancel, delete,
  // reassign — asks `Installation.Configure`; InstitutionAdmin holds `Installation.View` only and
  // is read-only. Contacting the installer is a `mailto:` from the detail, so it rides on the read.
  // UI-ONLY until HS-1621 lands; the screens write to the mock transport meanwhile.
  'installation.create': ['Installation.Configure'],
  'installation.edit': ['Installation.Configure'],
  'installation.cancel': ['Installation.Configure'],
  'installation.delete': ['Installation.Configure'],
  'installation.reassign': ['Installation.Configure'],
  'installation.contactInstaller': ['Installation.View'],

  // ── Service requests ──────────────────────────────────────────────────────────────────────
  // HS-1548, a mirror: `ServiceRequestsController` asks `Request.Manage` on both
  // `POST /api/service-requests/{id}/complete` and `…/reject`. One action because the two
  // outcomes are one decision in one dialog. The attribute is only a coarse gate — the service
  // also requires `Request.Manage` on the request's own institution and refuses the caller's own
  // request (`SERVICE_REQUEST_FORBIDDEN`, `SERVICE_REQUEST_SELF_DECISION`), which no key can mirror.
  'serviceRequest.decide': ['Request.Manage'],
  // HS-1542, a mirror: `PUT /api/service-requests/{id}/assignee` and `GET …/assignees` ask
  // `Request.Manage`. The service also requires it on the request's own institution, so a 403 can
  // still come back for a row of another institution. The dashboard card keeps
  // `dashboard.assignAlert`, which needs the same key.
  'serviceRequest.assign': ['Request.Manage'],

  // ── Reports ───────────────────────────────────────────────────────────────────────────────
  // HS-961, enforced by the Admin API since HS-1592 (AdminAPI `ReportController`): `POST
  // /api/reports/run` and `…/runs/{id}/retry` ask `Report.Export`, as does `GET
  // …/runs/{id}/download/{format}`; every `/schedules*` route and `/recipients` ask it too. Three
  // actions, one key today — the seam a separate `Report.Schedule` would land in if product
  // introduces one.
  'report.generate': ['Report.Export'],
  'report.download': ['Report.Export'],
  'report.schedule': ['Report.Export'],
  // HS-961 Amendment B (QB2): `DELETE /api/reports/runs/{id}` is a soft delete that asks
  // `Report.Export` (HS-1592 permission table, semantics in HS-1595). Its own entry, beside
  // generate and download, so a narrower delete key would be one edit here.
  'report.delete': ['Report.Export'],

  // ── My Files (portal) ─────────────────────────────────────────────────────────────────────
  // A mirror, not a product decision: `PortalFilesController` carries these exact keys on the
  // upload and the delete routes, and neither is mandatory — an institution can revoke either
  // one, which is precisely the case these entries exist to draw. Reading and downloading stay
  // ungated, on the controller as well as here.
  'file.upload': ['File.Upload'],
  'file.delete': ['File.Delete'],
};

/**
 * Whether `subject` may perform `actionId`. Actions requiring nothing are open to any
 * authenticated subject; otherwise the subject needs at least one of the action's keys.
 */
export function canAction(actionId: ActionId, subject: Subject): boolean {
  const required = ACTION_PERMISSIONS[actionId];
  if (required.length === 0) return true;
  return required.some((key) => hasPermission(subject, key));
}
